import React, { FC, useContext, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { UniqueSourcesIconList, CustomActions } from 'src/common/components';
import { ReactComponent as CopyUrlIcon } from 'src/common/images/copy-url.svg';
import { copyToClipboard } from 'src/common/utils/communication';
import { i18n } from 'src/common/i18n/i18n';
import { ReactComponent as CopyIcon } from 'src/common/images/copy.svg';
import Popup from 'src/common/components/Popup/Popup';
import { COLORS, SECTION } from 'src/common/constants/constants';
import { AddTagIcon } from '@cellebrite/saas-ui-icons';
import TagsManager from 'src/common/components/TagsManager/TagsManager';
import TagsViewer from 'src/common/components/TagsViewer/TagsViewer';
import { GlobalContext } from 'src/common/contexts/globalContext';
import { Element } from 'react-scroll';
import { SmartImage } from '@cellebrite/saas-ui-common';
import MultiMarkedText from 'src/common/components/MultiMarkedText/MultiMarkedText';
import { getKeywordsFromWatchLists, multiMarking } from 'src/common/utils/utils';
import ViewWatchLists from 'src/common/components/ViewWatchLists/ViewWatchLists';

interface WebMentionRowProps {
    isPossibleMatches?: boolean;
    webMention: any;
    findInput?: string;
}

const WebMentionRow: FC<WebMentionRowProps> = ({ isPossibleMatches, webMention, findInput }) => {
    const { reportData } = useContext(GlobalContext);
    const [showActions, setShowActions] = useState(false);
    const [actionsOpen, setActionsOpen] = useState(false);
    const [showTag, setShowTag] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const betweenTagsRegex = '(?![^<>]*>)';
    const escapeRegexSpecialCharacters = (string) => string?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapeTags = (string) => {
        let myHTML = new DOMParser().parseFromString(string, 'text/html');
        return myHTML.body.textContent || '';
    };
    const regSearchTerm = escapeRegexSpecialCharacters(webMention.searchTerm);
    const regFindInput = escapeRegexSpecialCharacters(findInput);
    const getMarkedText = (text, type) => {
        let result;
        if (text) {
            text = escapeTags(text);
            if (findInput?.length && text.toLowerCase().includes(findInput.toLowerCase())) {
                let iterator = 0;
                result = regSearchTerm
                    ? text
                          .replaceAll(new RegExp(regSearchTerm, 'gi'), '<mark class="background-color-mdh">$&</mark>')
                          .replaceAll(
                              new RegExp(regFindInput + betweenTagsRegex, 'gi'),
                              '<mark style="background-color: #FFECB2">$&</mark>',
                          )
                    : text.replaceAll(
                          new RegExp(regFindInput + betweenTagsRegex, 'gi'),
                          '<mark style="background-color: #FFECB2">$&</mark>',
                      );
                if (
                    reportData.currentFindLink?.id === webMention.identifier &&
                    reportData.currentFindLink?.type === type
                ) {
                    result = result.replace(/#FFECB2/gi, (match) =>
                        ++iterator === reportData.currentFindLink?.index + 1 ? COLORS.WARNING_LIGHTEST : match,
                    );
                }
            } else {
                result = text.replaceAll(
                    new RegExp(regSearchTerm, 'gi'),
                    '<mark class="background-color-mdh">$&</mark>',
                );
            }
        }
        return result;
    };

    const webMentionKeywords = getKeywordsFromWatchLists(
        webMention.watchlist,
        reportData.currentHighlightsWatchlist?.id,
    );

    return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <Element style={{ position: 'absolute' }} name={webMention.identifier + SECTION.WEB_MENTIONS}></Element>
            <Box
                sx={{
                    borderRadius: '8px',
                    height: 128,
                    p: 1,
                    width: webMention.imageUrl ? '100%' : 'calc(100% - 208px)',
                    display: 'flex',
                }}
                className={showActions || actionsOpen || showTag || tagOpen ? 'background-color-mho' : ''}
                onMouseEnter={() => {
                    setShowTag(true);
                    setShowActions(true);
                }}
                onMouseLeave={() => {
                    setShowTag(false);
                    setShowActions(false);
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mr: 3,
                        width: webMention.imageUrl ? 'calc(100% - 233px)' : '100%',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', height: 24, overflow: 'hidden', columnGap: 1 }}
                        >
                            {isPossibleMatches || !reportData.collection?.taggable ? null : (
                                <TagsViewer checkedTags={webMention.tags} />
                            )}
                            {!!webMention.watchlist?.length && <ViewWatchLists watchLists={webMention.watchlist} />}
                            <SmartImage src={webMention.favicon} containerStyles={{ width: 16, height: 16 }} />
                            {reportData.displayHighlights ? (
                                <MultiMarkedText
                                    containerProps={{
                                        title: escapeTags(webMention.url),
                                        noWrap: true,
                                        variant: 's12w400',
                                        className: 'color-bd',
                                        sx: Object.assign(
                                            {
                                                ml: 0.5,
                                                mr: 0.5,
                                                overflow: 'hidden',
                                                maxWidth: 650,
                                            },
                                            multiMarking(
                                                webMention.identifier,
                                                SECTION.WEB_MENTIONS,
                                                'url',
                                                reportData.currentHighlightsLink,
                                            ),
                                        ),
                                    }}
                                    searchTextArr={webMentionKeywords}
                                >
                                    {escapeTags(webMention.url)}
                                </MultiMarkedText>
                            ) : (
                                <Typography
                                    title={webMention.url}
                                    noWrap
                                    sx={{ ml: 0.5, mr: 0.5, overflow: 'hidden', maxWidth: 650 }}
                                    variant="s12w400"
                                    className="color-bd"
                                    dangerouslySetInnerHTML={{
                                        __html: getMarkedText(webMention.url, 'url'),
                                    }}
                                ></Typography>
                            )}
                            <IconButton onClick={() => copyToClipboard(webMention.url)}>
                                <CopyUrlIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                            <UniqueSourcesIconList originalSources={webMention.sources} listShouldWrap={false} />
                            <CustomActions
                                onToggleActions={() => setActionsOpen(!actionsOpen)}
                                containerStyles={{
                                    pl: 1,
                                    visibility: showActions || actionsOpen || showTag || tagOpen ? 'visible' : 'hidden',
                                }}
                                actions={[
                                    {
                                        name: i18n.translate('Go to the Web Mention'),
                                        icon: <CopyIcon />,
                                        disabled: !webMention.url,
                                        onClick: () => window.open(webMention.url, '_blank', 'noopener,noreferrer'),
                                    },
                                ]}
                                prefixElement={
                                    isPossibleMatches || !reportData.collection?.taggable ? null : (
                                        <Box sx={{ ml: 0.5, mr: 0.5, minWidth: '24px' }}>
                                            {(showTag || tagOpen) && (
                                                <Popup
                                                    onToggle={() => {
                                                        setTagOpen(!tagOpen);
                                                    }}
                                                    actionElement={(handleOpen, anchorRef, open) => {
                                                        return (
                                                            <Box ref={anchorRef}>
                                                                <IconButton
                                                                    sx={{
                                                                        'backgroundColor': tagOpen
                                                                            ? COLORS.MUTED_DOUBLE_HOVER
                                                                            : null,
                                                                        '&:hover': {
                                                                            backgroundColor: COLORS.MUTED_DOUBLE_HOVER,
                                                                        },
                                                                    }}
                                                                    disableRipple
                                                                    onClick={handleOpen}
                                                                >
                                                                    <AddTagIcon
                                                                        fill={open ? COLORS.BRAND : COLORS.MUTED_DARK}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                        );
                                                    }}
                                                    contentElement={(handleClose) => (
                                                        <Box>
                                                            <TagsManager
                                                                checkedTags={webMention.tags}
                                                                section={SECTION.WEB_MENTIONS}
                                                                elementId={webMention.identifier}
                                                            />
                                                        </Box>
                                                    )}
                                                />
                                            )}
                                        </Box>
                                    )
                                }
                            />
                        </Box>
                    </Box>
                    {reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                title: escapeTags(webMention.searchResultTitle),
                                variant: 's18w500',
                                className: 'color-bd',
                                sx: Object.assign(
                                    {
                                        mt: 1,
                                        height: 24,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                    multiMarking(
                                        webMention.identifier,
                                        SECTION.WEB_MENTIONS,
                                        'searchResultTitle',
                                        reportData.currentHighlightsLink,
                                    ),
                                ),
                            }}
                            searchTextArr={webMentionKeywords}
                        >
                            {escapeTags(webMention.searchResultTitle)}
                        </MultiMarkedText>
                    ) : (
                        <Typography
                            title={webMention.searchResultTitle}
                            sx={{
                                mt: 1,
                                height: 24,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            variant="s18w500"
                            className="color-bd"
                            dangerouslySetInnerHTML={{
                                __html: getMarkedText(webMention.searchResultTitle, 'searchResultTitle'),
                            }}
                        ></Typography>
                    )}
                    {reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                title: escapeTags(webMention.searchResultSnippet),
                                variant: 's16w400',
                                className: 'color-bd',
                                sx: Object.assign(
                                    {
                                        mt: 1,
                                        height: 'calc(100% - 64px)',
                                        display: '-webkit-box',
                                        WebkitLineClamp: '2',
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    },
                                    multiMarking(
                                        webMention.identifier,
                                        SECTION.WEB_MENTIONS,
                                        'searchResultSnippet',
                                        reportData.currentHighlightsLink,
                                    ),
                                ),
                            }}
                            searchTextArr={webMentionKeywords}
                        >
                            {escapeTags(webMention.searchResultSnippet)}
                        </MultiMarkedText>
                    ) : (
                        <Typography
                            title={webMention.searchResultSnippet}
                            sx={{
                                mt: 1,
                                height: 'calc(100% - 64px)',
                                display: '-webkit-box',
                                WebkitLineClamp: '2',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                            variant="s16w400"
                            className="color-bd"
                            dangerouslySetInnerHTML={{
                                __html: getMarkedText(webMention.searchResultSnippet, 'searchResultSnippet'),
                            }}
                        ></Typography>
                    )}
                </Box>
                {webMention.imageUrl && (
                    <SmartImage
                        src={webMention.imageUrl}
                        containerStyles={{ minWidth: 199, width: 199, height: '100%' }}
                        roundedCorners={true}
                    />
                )}
            </Box>
        </Box>
    );
};

export default WebMentionRow;
